
import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import StructuredData from "@/components/StructuredData";

// Import refactored components
import HeroSection from "@/components/location-bennes/HeroSection";
import ServiceDescription from "@/components/location-bennes/ServiceDescription";
import BenneTypesCard from "@/components/location-bennes/BenneTypesCard";
import HowItWorksSection from "@/components/location-bennes/HowItWorksSection";
import RequestFormSection from "@/components/location-bennes/RequestFormSection";
import ServiceZones from "@/components/location-bennes/ServiceZones";
import LocationBennesSEOContent from "@/components/seo/LocationBennesSEOContent";
import { departements, majorCities } from "@/components/location-bennes/LocationData";

const LocationBennes = () => {
  // Schema.org Service data optimisé pour le SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.groupefabre-environnement.fr/nos-services/location-bennes#service",
    "name": "Location de bennes pour déchets - Service professionnel",
    "description": "Service professionnel de location de bennes pour la collecte et l'évacuation de déchets. Solutions adaptées aux particuliers, professionnels et collectivités dans 8 départements français. Tarifs à partir de 120€, livraison 24-48h.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Groupe Fabre Environnement",
      "url": "https://www.groupefabre-environnement.fr",
      "telephone": "+33565787900",
      "email": "contactgfe@groupefabre.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Rodez",
        "postalCode": "12000",
        "addressRegion": "Occitanie",
        "addressCountry": "FR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "44.3500",
        "longitude": "2.5833"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "127",
        "bestRating": "5",
        "worstRating": "1"
      }
    },
    "serviceType": "Location d'équipement de collecte de déchets",
    "category": ["Gestion des déchets", "Location de matériel", "Environnement"],
    "audience": {
      "@type": "Audience",
      "audienceType": "Particuliers, professionnels, entreprises BTP, collectivités"
    },
    "areaServed": [
      {
        "@type": "AdministrativeArea",
        "name": "Aveyron (12)",
        "containsPlace": [
          {"@type": "City", "name": "Rodez"},
          {"@type": "City", "name": "Millau"},
          {"@type": "City", "name": "Villefranche-de-Rouergue"}
        ]
      },
      {
        "@type": "AdministrativeArea", 
        "name": "Haute-Garonne (31)",
        "containsPlace": [
          {"@type": "City", "name": "Toulouse"},
          {"@type": "City", "name": "Muret"},
          {"@type": "City", "name": "Saint-Gaudens"}
        ]
      },
      {
        "@type": "AdministrativeArea",
        "name": "Tarn (81)",
        "containsPlace": [
          {"@type": "City", "name": "Albi"},
          {"@type": "City", "name": "Castres"},
          {"@type": "City", "name": "Mazamet"}
        ]
      }
    ],
    "offers": [
      {
        "@type": "Offer",
        "name": "Benne gravats 8m³",
        "description": "Benne spécialisée pour gravats, béton, démolition",
        "price": "120",
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock",
        "itemOffered": {
          "@type": "Product",
          "name": "Benne gravats 8m³",
          "category": "Conteneur déchets"
        }
      },
      {
        "@type": "Offer", 
        "name": "Benne tout-venant 15m³",
        "description": "Benne polyvalente pour déchets ménagers et encombrants",
        "price": "180",
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock",
        "itemOffered": {
          "@type": "Product",
          "name": "Benne tout-venant 15m³",
          "category": "Conteneur déchets"
        }
      },
      {
        "@type": "Offer",
        "name": "Benne grande capacité 30m³", 
        "description": "Benne pour gros chantiers et professionnels BTP",
        "price": "250",
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock",
        "itemOffered": {
          "@type": "Product",
          "name": "Benne grande capacité 30m³",
          "category": "Conteneur déchets"
        }
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Catalogue de bennes",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Benne gravats 8m³",
            "description": "Parfaite pour évacuer béton, briques, tuiles, carrelage"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product", 
            "name": "Benne tout-venant 15m³",
            "description": "Idéale pour déchets ménagers, encombrants, DIB"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Benne déchets verts 20m³", 
            "description": "Spécialisée pour branches, feuilles, tontes de gazon"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Benne grande capacité 30m³",
            "description": "Pour gros chantiers, entreprises BTP, collectivités"
          }
        }
      ]
    },
    "availableChannel": [
      {
        "@type": "ServiceChannel",
        "servicePhone": "+33565787900",
        "serviceName": "Commande téléphonique"
      },
      {
        "@type": "ServiceChannel", 
        "serviceUrl": "https://www.groupefabre-environnement.fr/nos-services/location-bennes",
        "serviceName": "Commande en ligne"
      }
    ]
  };

  // Schema.org HowTo optimisé
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "Comment louer une benne avec Groupe Fabre Environnement",
    "description": "Guide complet pour louer une benne facilement et rapidement. Service professionnel en 4 étapes simples.",
    "image": "https://www.groupefabre-environnement.fr/images/location-bennes.jpg",
    "totalTime": "PT30M",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "EUR",
      "value": "150"
    },
    "supply": [
      {
        "@type": "HowToSupply",
        "name": "Informations projet"
      },
      {
        "@type": "HowToSupply", 
        "name": "Type et quantité de déchets"
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": "Téléphone ou formulaire en ligne"
      }
    ],
    "step": [
      {
        "@type": "HowToStep",
        "name": "1. Évaluation de vos besoins",
        "text": "Contactez-nous au 05 65 78 79 00 ou remplissez notre formulaire en ligne. Précisez le type de déchets, la quantité estimée, la durée de location et l'adresse de livraison.",
        "position": 1,
        "url": "https://www.groupefabre-environnement.fr/nos-services/location-bennes#request-form"
      },
      {
        "@type": "HowToStep",
        "name": "2. Devis gratuit et conseil personnalisé",
        "text": "Nous vous proposons la benne adaptée à votre projet avec un devis transparent. Nos experts vous conseillent sur la taille optimale selon votre volume de déchets.",
        "position": 2
      },
      {
        "@type": "HowToStep",
        "name": "3. Livraison rapide sous 24-48h",
        "text": "Livraison de votre benne à l'endroit et à la date convenus. Notre équipe s'assure du bon positionnement selon vos contraintes d'accès et de sécurité.",
        "position": 3
      },
      {
        "@type": "HowToStep",
        "name": "4. Collecte et valorisation écologique",
        "text": "À la fin de la période convenue, nous récupérons la benne pleine et traitons vos déchets selon nos standards environnementaux avec un taux de valorisation de 85%.",
        "position": 4
      }
    ]
  };

  // Schema FAQ optimisé
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Quel est le prix d'une location de benne ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Nos tarifs de location de bennes démarrent à 120€ pour une benne gravats 8m³. Le prix varie selon la taille (8m³ à 30m³), la durée de location et le type de déchets. Nous proposons des devis gratuits personnalisés."
        }
      },
      {
        "@type": "Question",
        "name": "Dans quels départements proposez-vous la location de bennes ?",
        "acceptedAnswer": {
          "@type": "Answer", 
          "text": "Nous intervenons dans 8 départements : Aveyron (12), Cantal (15), Haute-Garonne (31), Lot (46), Lozère (48), Puy-de-Dôme (63), Tarn (81) et Val-d'Oise (95) avec livraison rapide en 24-48h."
        }
      },
      {
        "@type": "Question",
        "name": "Quels types de déchets puis-je mettre dans la benne ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Selon le type de benne : gravats et béton pour les bennes gravats, déchets verts pour les bennes spécialisées, encombrants et DIB pour les bennes tout-venant. Nous vous conseillons sur le tri optimal."
        }
      },
      {
        "@type": "Question", 
        "name": "Combien de temps puis-je garder la benne ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "La durée standard de location est de 7 jours, extensible selon vos besoins de chantier. Nous nous adaptons à votre planning pour une flexibilité maximale."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-white">
      <SEO 
        title="Location de Bennes Professionnelle - Tarifs dès 120€ | Groupe Fabre"
        description="Location de bennes dans l'Aveyron, Cantal, Tarn, Haute-Garonne, Lot, Lozère, Puy-de-Dôme et Val-d'Oise. Tarifs dès 120€, livraison 24-48h, devis gratuit. Bennes gravats, tout-venant, déchets verts."
        canonical="/nos-services/location-bennes"
        keywords="location benne, benne gravats, benne déchets, location conteneur, évacuation déchets, Aveyron, Toulouse, Albi, Rodez, tarif benne, devis benne"
      />
      
      {/* Structured Data */}
      <StructuredData data={serviceSchema} />
      <StructuredData data={howToSchema} />
      <StructuredData data={faqSchema} />
      
      <Navbar />
      
      <main className="flex-1 pt-20">
        {/* Enhanced Hero Section */}
        <div className="relative overflow-hidden">
          <HeroSection />
          {/* Decorative wave */}
          <div className="absolute bottom-0 left-0 w-full overflow-hidden">
            <svg className="relative block w-full h-[40px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none">
              <path fill="#f9fafb" fillOpacity="1" d="M0,32L80,42.7C160,53,320,75,480,74.7C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,100L1360,100C1280,100,1120,100,960,100C800,100,640,100,480,100C320,100,160,100,80,100L0,100Z"></path>
            </svg>
          </div>
        </div>

        {/* Breadcrumb with enhanced styling */}
        <nav aria-label="Fil d'Ariane" className="bg-white py-4 border-b border-gray-100 shadow-sm">
          <div className="container mx-auto px-4">
            <Breadcrumb
              items={[
                { label: "Services", href: "/nos-services" },
                { label: "Location de bennes", href: "/nos-services/location-bennes", current: true }
              ]}
            />
          </div>
        </nav>

        {/* Main content with improved spacing and visual hierarchy */}
        <article className="py-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            {/* Enhanced SEO content section */}
            <section className="mb-20">
              <LocationBennesSEOContent />
            </section>
            
            {/* Service overview with better grid layout */}
            <section className="mb-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
                  <ServiceDescription />
                </div>
                <div className="bg-gradient-to-br from-fabre-lightgreen to-white rounded-3xl shadow-xl p-8 border border-fabre-green/20 hover:shadow-2xl transition-all duration-300">
                  <BenneTypesCard />
                </div>
              </div>
            </section>

            {/* How it works section with enhanced design */}
            <section aria-labelledby="how-it-works" className="mb-20">
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                <div className="bg-gradient-to-r from-fabre-green to-fabre-teal p-8 text-white">
                  <h2 id="how-it-works" className="text-3xl font-heading font-bold text-center">
                    Comment ça marche ?
                  </h2>
                  <div className="w-24 h-1 bg-white/30 mx-auto mt-4 rounded-full"></div>
                </div>
                <div className="p-8">
                  <HowItWorksSection />
                </div>
              </div>
            </section>
            
            {/* Request form with enhanced styling */}
            <section aria-labelledby="request-form" className="mb-20">
              <div className="bg-gradient-to-br from-fabre-lightgreen to-white rounded-3xl shadow-2xl overflow-hidden border border-fabre-green/30">
                <div className="bg-gradient-to-r from-fabre-green to-fabre-darkgreen p-8 text-white">
                  <h2 id="request-form" className="text-3xl font-heading font-bold text-center">
                    Demandez votre devis gratuit
                  </h2>
                  <div className="w-24 h-1 bg-white/30 mx-auto mt-4 rounded-full"></div>
                  <p className="text-center mt-4 opacity-90">
                    Réponse sous 2h • Livraison 24-48h • Sans engagement
                  </p>
                </div>
                <div className="p-8">
                  <RequestFormSection />
                </div>
              </div>
            </section>
            
            {/* Service zones with improved layout */}
            <section aria-labelledby="service-zones" className="mb-20">
              <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                <div className="text-center mb-12">
                  <h2 id="service-zones" className="text-3xl font-heading font-bold text-fabre-gray mb-4">
                    Nos zones d'intervention
                  </h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-fabre-green to-fabre-teal mx-auto mb-6 rounded-full"></div>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    Service disponible dans 8 départements avec livraison rapide
                  </p>
                </div>
                <ServiceZones departements={departements} majorCities={majorCities} />
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default LocationBennes;
